/**
 * Rendu des noeuds de ressources recoltables: arbres, palmiers, rochers,
 * buissons a baies, cactus et blocs de glace.
 *
 * Tout passe par un `InstancedMesh` par espece, plus un `InstancedMesh` de
 * souches. Un noeud epuise voit son instance mise a l'echelle zero et une
 * souche apparaitre a sa place; quand il repousse, on inverse. Deux appels de
 * dessin par espece, quelques milliers de noeuds, aucun cout par frame.
 *
 * NIVEAU DE DETAIL. Chaque espece a DEUX geometries: la complete, et une
 * silhouette a la meme place et au meme volume mais bien moins de facettes. Ce
 * n'est pas un escamotage comme celui de l'herbe (`PIXELS_UTILES` dans
 * props.ts): un arbre reste visible a tous les zooms, seule sa finesse change.
 * On echange la geometrie du maillage d'instances, pas le maillage: les
 * matrices d'instance et les spheres englobantes restent en place, et le nombre
 * d'appels de dessin ne bouge pas d'un seul.
 */
import * as THREE from 'three'
import type { Batiment, Ressource, RessourceKind } from '../../shared/types/monde.ts'
import { estDisponible } from '../../shared/sim/ressources.ts'
import { couvreLePoint, rayonCouverture } from '../../shared/sim/batiments.ts'
import { clamp } from '../../shared/sim/alea.ts'
import { CHUNK_SIZE } from '../../shared/constants.ts'
import { journal } from '../log/journal.ts'
import { bosseler, fusionner, tr } from './geometrie.ts'

/** Duree de la secousse quand on frappe un noeud, en secondes. */
const DUREE_SECOUSSE = 0.32

// -------------------------------------------------------------- geometries

function geoArbre(): THREE.BufferGeometry {
  // Arbre de foret tropicale: tronc elance, trois etages de feuillage.
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.16, 0.26, 4.2, 7), couleur: 0x6b4a2c, transforme: tr([0, 2.1, 0]) },
    { geo: new THREE.SphereGeometry(1.5, 10, 7), couleur: 0x2f7d3a, transforme: tr([0, 4.3, 0], undefined, [1, 0.72, 1]) },
    { geo: new THREE.SphereGeometry(1.2, 10, 7), couleur: 0x3d9b48, transforme: tr([0.35, 5.2, -0.2], undefined, [1, 0.7, 1]) },
    { geo: new THREE.SphereGeometry(0.85, 9, 6), couleur: 0x4fb356, transforme: tr([-0.3, 5.9, 0.25], undefined, [1, 0.75, 1]) },
  ])
}

function geoPalmier(): THREE.BufferGeometry {
  const parties: { geo: THREE.BufferGeometry; couleur: number; transforme: THREE.Matrix4 }[] = [
    { geo: new THREE.CylinderGeometry(0.14, 0.22, 5, 7), couleur: 0x9c7a4a, transforme: tr([0.35, 2.5, 0], [0, 0, -0.14]) },
  ]
  // Six palmes en couronne, retombantes.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2
    parties.push({
      geo: new THREE.SphereGeometry(1.25, 7, 4),
      couleur: i % 2 === 0 ? 0x3f9f52 : 0x51b45f,
      transforme: tr(
        [0.7 + Math.cos(a) * 0.95, 4.85 - Math.abs(Math.sin(a * 1.3)) * 0.35, Math.sin(a) * 0.95],
        [0, a, 0.42],
        [1, 0.14, 0.38],
      ),
    })
  }
  parties.push({
    geo: new THREE.SphereGeometry(0.19, 7, 5),
    couleur: 0x8b6b3f,
    transforme: tr([0.6, 4.6, 0.18]),
  })
  return fusionner(parties)
}

function geoRocher(): THREE.BufferGeometry {
  return fusionner([
    { geo: bosseler(new THREE.DodecahedronGeometry(0.95, 0), 0.34, 3), couleur: 0x8c8f99, transforme: tr([0, 0.7, 0], undefined, [1, 0.78, 1]) },
    { geo: bosseler(new THREE.DodecahedronGeometry(0.45, 0), 0.3, 11), couleur: 0x9ba0a8, transforme: tr([0.7, 0.35, 0.35]) },
  ])
}

function geoBuisson(): THREE.BufferGeometry {
  const parties = [
    { geo: new THREE.SphereGeometry(0.72, 9, 7), couleur: 0x2f7a3c, transforme: tr([0, 0.62, 0], undefined, [1, 0.85, 1]) },
    { geo: new THREE.SphereGeometry(0.5, 8, 6), couleur: 0x3d9147, transforme: tr([0.45, 0.45, 0.2]) },
    { geo: new THREE.SphereGeometry(0.44, 8, 6), couleur: 0x37853f, transforme: tr([-0.4, 0.42, -0.25]) },
  ]
  // Les baies: c'est ce qui rend le buisson identifiable comme recoltable.
  const points: [number, number, number][] = [
    [0.3, 0.95, 0.25],
    [-0.25, 1.0, 0.15],
    [0.1, 1.15, -0.3],
    [0.55, 0.7, -0.2],
    [-0.5, 0.75, 0.35],
    [0.0, 0.8, 0.6],
  ]
  for (const p of points) {
    parties.push({ geo: new THREE.SphereGeometry(0.11, 6, 5), couleur: 0xd23a5a, transforme: tr(p) })
  }
  return fusionner(parties)
}

function geoCactus(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.32, 0.36, 2.4, 9), couleur: 0x3f8f52, transforme: tr([0, 1.2, 0]) },
    { geo: new THREE.SphereGeometry(0.33, 9, 6), couleur: 0x47a05b, transforme: tr([0, 2.4, 0]) },
    { geo: new THREE.CylinderGeometry(0.17, 0.19, 1.0, 8), couleur: 0x47a05b, transforme: tr([0.48, 1.55, 0], [0, 0, -0.45]) },
    { geo: new THREE.SphereGeometry(0.18, 8, 6), couleur: 0x4fac62, transforme: tr([0.66, 1.95, 0]) },
    { geo: new THREE.CylinderGeometry(0.15, 0.17, 0.8, 8), couleur: 0x47a05b, transforme: tr([-0.42, 1.15, 0.1], [0, 0, 0.5]) },
    { geo: new THREE.SphereGeometry(0.16, 8, 6), couleur: 0x4fac62, transforme: tr([-0.58, 1.48, 0.1]) },
    { geo: new THREE.SphereGeometry(0.13, 7, 5), couleur: 0xf2b8c6, transforme: tr([0, 2.66, 0]) },
  ])
}

function geoGlace(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.IcosahedronGeometry(0.85, 0), couleur: 0xbfe7f7, transforme: tr([0, 0.72, 0], [0.2, 0.4, 0.1]) },
    { geo: new THREE.IcosahedronGeometry(0.5, 0), couleur: 0xdff2fb, transforme: tr([0.55, 0.4, -0.3], [0.4, 0.9, 0.2]) },
    { geo: new THREE.ConeGeometry(0.3, 1.1, 6), couleur: 0xa9dcf0, transforme: tr([-0.4, 0.85, 0.35], [0.12, 0, -0.1]) },
  ])
}

/**
 * LE CRISTAL du canyon: un bouquet de prismes qui sortent d'une gangue.
 *
 * Il est fait pour etre lu DE DESSUS, a quarante-cinq degres — la lecon du
 * front de taille de la mine, ou une paroi haute et mince disparaissait pour
 * deux rotations sur quatre. Les prismes sont donc inclines dans des directions
 * DIFFERENTES: quel que soit le cote d'ou l'on regarde, il y en a au moins un
 * qui se presente de profil et un qui accroche la lumiere a plat.
 */
function geoCristal(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.DodecahedronGeometry(0.52, 0), couleur: 0x6f6a8c, transforme: tr([0, 0.2, 0], [0.1, 0.3, 0], [1.25, 0.5, 1.25]) },
    { geo: new THREE.ConeGeometry(0.3, 1.5, 6), couleur: 0xb69ce8, transforme: tr([0, 0.85, 0], [0.06, 0.4, 0.04]) },
    { geo: new THREE.ConeGeometry(0.22, 1.05, 6), couleur: 0xd9c4f6, transforme: tr([0.42, 0.6, 0.18], [0.1, 0, -0.34]) },
    { geo: new THREE.ConeGeometry(0.18, 0.82, 6), couleur: 0x9d86d8, transforme: tr([-0.34, 0.52, -0.3], [-0.22, 0.8, 0.28]) },
    { geo: new THREE.ConeGeometry(0.14, 0.6, 6), couleur: 0xe4d8fb, transforme: tr([-0.12, 0.42, 0.44], [0.3, 0.2, 0.12]) },
  ])
}

/**
 * L'ARBRE LILAS: la silhouette d'un arbre, la couleur d'un lilas.
 *
 * Il reprend volontairement la STRUCTURE de `geoArbre` — meme tronc elance,
 * memes trois etages de feuillage — parce que c'est ce qui le fait lire comme
 * un arbre au premier coup d'oeil, donc comme quelque chose qu'on coupe. Seule
 * la couleur change, et quelques grappes qui pendent.
 */
function geoArbreLilas(): THREE.BufferGeometry {
  const parties = [
    { geo: new THREE.CylinderGeometry(0.15, 0.25, 3.9, 7), couleur: 0x6d5242, transforme: tr([0, 1.95, 0]) },
    { geo: new THREE.SphereGeometry(1.45, 10, 7), couleur: 0x8f5fb5, transforme: tr([0, 4.05, 0], undefined, [1, 0.72, 1]) },
    { geo: new THREE.SphereGeometry(1.15, 10, 7), couleur: 0xb078d2, transforme: tr([0.32, 4.9, -0.2], undefined, [1, 0.7, 1]) },
    { geo: new THREE.SphereGeometry(0.82, 9, 6), couleur: 0xd9a8e8, transforme: tr([-0.3, 5.55, 0.24], undefined, [1, 0.75, 1]) },
  ]
  // Les grappes: c'est ce qui distingue un lilas d'un arbre repeint.
  const grappes: [number, number, number][] = [
    [1.15, 3.6, 0.2],
    [-1.05, 3.75, -0.3],
    [0.25, 3.4, 1.1],
    [-0.35, 4.4, -1.05],
  ]
  for (const g of grappes) {
    parties.push({
      geo: new THREE.ConeGeometry(0.26, 0.8, 6),
      couleur: 0xc98fe0,
      transforme: tr(g, [Math.PI, 0, 0]),
    })
  }
  return fusionner(parties)
}

function geoArbreLilasLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.16, 0.24, 3.9, 5), couleur: 0x6d5242, transforme: tr([0, 1.95, 0]) },
    { geo: new THREE.SphereGeometry(1.5, 7, 5), couleur: 0xa46bc8, transforme: tr([0, 4.3, 0], undefined, [1, 0.78, 1]) },
  ])
}

function geoCristalLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.ConeGeometry(0.34, 1.5, 5), couleur: 0xbfa6ec, transforme: tr([0, 0.82, 0], [0.06, 0.4, 0.04]) },
  ])
}

function geoSouche(kind: RessourceKind): THREE.BufferGeometry {
  if (kind === 'cristal') {
    // Ce qui reste d'un cristal pioche: la gangue, et deux eclats casses.
    return fusionner([
      { geo: new THREE.DodecahedronGeometry(0.5, 0), couleur: 0x6f6a8c, transforme: tr([0, 0.12, 0], [0.1, 0.3, 0], [1.2, 0.35, 1.2]) },
      { geo: new THREE.ConeGeometry(0.16, 0.3, 6), couleur: 0x9d86d8, transforme: tr([0.2, 0.24, -0.14], [0.12, 0, -0.3]) },
    ])
  }
  if (kind === 'rocher' || kind === 'bloc_glace') {
    const couleur = kind === 'rocher' ? 0x76797f : 0xcfe6f2
    return fusionner([
      { geo: new THREE.CylinderGeometry(0.7, 0.8, 0.2, 9), couleur, transforme: tr([0, 0.1, 0]) },
    ])
  }
  if (kind === 'buisson_baies' || kind === 'cactus') {
    return fusionner([
      { geo: new THREE.SphereGeometry(0.4, 8, 5), couleur: 0x5f7a4a, transforme: tr([0, 0.16, 0], undefined, [1, 0.4, 1]) },
    ])
  }
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.26, 0.32, 0.42, 8), couleur: 0x6b4a2c, transforme: tr([0, 0.21, 0]) },
    { geo: new THREE.CylinderGeometry(0.27, 0.27, 0.06, 8), couleur: 0xb08d63, transforme: tr([0, 0.43, 0]) },
  ])
}

const FABRIQUES: Record<RessourceKind, () => THREE.BufferGeometry> = {
  arbre: geoArbre,
  palmier: geoPalmier,
  rocher: geoRocher,
  buisson_baies: geoBuisson,
  cactus: geoCactus,
  bloc_glace: geoGlace,
  cristal: geoCristal,
  arbre_lilas: geoArbreLilas,
}

// ------------------------------------------------------- geometries de loin

/**
 * Silhouettes: meme encombrement, meme couleurs moyennes, six a dix fois moins
 * de facettes.
 *
 * La regle suivie pour chacune: garder ce qui donne la SILHOUETTE (un tronc
 * elance et deux masses de feuillage pour un arbre, une couronne pour un
 * palmier, une boule pour un buisson) et supprimer ce qui ne se lit qu'a
 * quelques metres (le troisieme etage de feuillage, les six baies, les bras du
 * cactus). Les segments radiaux tombent a cinq ou six: en vue orthographique de
 * dessus, un cylindre a cinq cotes et un cylindre a neuf cotes donnent le meme
 * pate de vingt pixels.
 */
function geoArbreLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.18, 0.28, 4.2, 4), couleur: 0x6b4a2c, transforme: tr([0, 2.1, 0]) },
    { geo: new THREE.SphereGeometry(1.58, 6, 4), couleur: 0x35893f, transforme: tr([0, 4.4, 0], undefined, [1, 0.76, 1]) },
    { geo: new THREE.SphereGeometry(1.05, 5, 3), couleur: 0x49a950, transforme: tr([0.1, 5.6, 0], undefined, [1, 0.78, 1]) },
  ])
}

function geoPalmierLoin(): THREE.BufferGeometry {
  const parties: { geo: THREE.BufferGeometry; couleur: number; transforme: THREE.Matrix4 }[] = [
    { geo: new THREE.CylinderGeometry(0.16, 0.24, 5, 4), couleur: 0x9c7a4a, transforme: tr([0.35, 2.5, 0], [0, 0, -0.14]) },
  ]
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2
    parties.push({
      geo: new THREE.SphereGeometry(1.35, 4, 3),
      couleur: 0x48a959,
      transforme: tr([0.7 + Math.cos(a) * 0.8, 4.8, Math.sin(a) * 0.8], [0, a, 0.42], [1, 0.2, 0.6]),
    })
  }
  return fusionner(parties)
}

function geoRocherLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: bosseler(new THREE.DodecahedronGeometry(1.05, 0), 0.3, 3), couleur: 0x91949d, transforme: tr([0, 0.72, 0], undefined, [1, 0.78, 1]) },
  ])
}

function geoBuissonLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.SphereGeometry(0.88, 6, 4), couleur: 0x357f42, transforme: tr([0, 0.66, 0], undefined, [1, 0.92, 1]) },
  ])
}

function geoCactusLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.CylinderGeometry(0.33, 0.36, 2.5, 5), couleur: 0x419453, transforme: tr([0, 1.25, 0]) },
    { geo: new THREE.CylinderGeometry(0.17, 0.19, 1.4, 4), couleur: 0x47a05b, transforme: tr([0.42, 1.7, 0], [0, 0, -0.45]) },
  ])
}

function geoGlaceLoin(): THREE.BufferGeometry {
  return fusionner([
    { geo: new THREE.IcosahedronGeometry(0.95, 0), couleur: 0xc4e9f8, transforme: tr([0, 0.75, 0], [0.2, 0.4, 0.1]) },
  ])
}

const FABRIQUES_LOIN: Record<RessourceKind, () => THREE.BufferGeometry> = {
  arbre: geoArbreLoin,
  palmier: geoPalmierLoin,
  rocher: geoRocherLoin,
  buisson_baies: geoBuissonLoin,
  cactus: geoCactusLoin,
  bloc_glace: geoGlaceLoin,
  cristal: geoCristalLoin,
  arbre_lilas: geoArbreLilasLoin,
}

/** Hauteur apparente de reference de chaque espece, en metres. */
const HAUTEUR_MODELE: Record<RessourceKind, number> = {
  arbre: 6.4,
  palmier: 5.4,
  rocher: 1.5,
  buisson_baies: 1.3,
  cactus: 2.8,
  bloc_glace: 1.7,
  cristal: 2.1,
  arbre_lilas: 6.1,
}

/**
 * Seuils de bascule, en pixels de hauteur apparente du modele.
 *
 * Au zoom de jeu (demi-frustum 9 m, ecran 900 px) un arbre pese 320 pixels: il
 * garde toute sa geometrie. Au dezoom maximum (45 m) il n'en pese plus que 64,
 * et la silhouette est indiscernable. Les deux seuils sont differents pour que
 * le lissage du zoom ne fasse pas clignoter la bascule.
 */
const PIXELS_LOIN = 100
const PIXELS_PRES = 132

// ------------------------------------------------------------------ classe

/**
 * Les noeuds sont regroupes par espece ET par chunk de terrain.
 *
 * Un `InstancedMesh` est dessine en entier ou pas du tout: avec un seul
 * maillage par espece, les 1152 arbres du monde partaient au GPU a chaque
 * image, et une seconde fois pour la carte d'ombre, alors qu'une poignee tient
 * dans le champ de la camera. Un maillage par (espece, chunk) porte une sphere
 * englobante serree, donc three.js elimine tout seul les lots hors du champ —
 * pour la camera comme pour la lumiere.
 */
interface Lot {
  kind: RessourceKind
  intact: THREE.InstancedMesh
  souche: THREE.InstancedMesh
  /** Les noeuds de ce lot, dans l'ordre des instances. */
  noeuds: Ressource[]
  /** Geometrie complete et silhouette, partagees avec les autres lots. */
  geoPres: THREE.BufferGeometry
  geoLoin: THREE.BufferGeometry
  /**
   * Noeuds recouverts par une construction (1 = escamote), ou null.
   *
   * Ni l'arbre ni sa souche ne s'affichent alors: on a abattu l'arbre POUR
   * batir, et le laisser repousser au milieu du salon — ou y laisser une souche
   * — revient au meme pour le joueur. `estDisponible` refuse par ailleurs de le
   * rendre recoltable, donc le rendu et la simulation disent la meme chose.
   */
  couvert: Uint8Array | null
}

interface Adresse {
  lot: Lot
  instance: number
}

interface Secousse {
  lot: Lot
  instance: number
  restant: number
}

export class RenduRessources {
  readonly groupe: THREE.Group
  private scene: THREE.Scene
  private lots: Lot[] = []
  /** Adresse de chaque noeud par identifiant. */
  private adresses = new Map<number, Adresse>()
  private materiau: THREE.MeshLambertMaterial
  private secousses: Secousse[] = []
  private anneau: THREE.Mesh
  private anneauVisible = false
  private anneauEchelle = 1
  private tempsAnneau = 0
  private matriceTemp = new THREE.Matrix4()
  private quatTemp = new THREE.Quaternion()
  private echelleTemp = new THREE.Vector3()
  private posTemp = new THREE.Vector3()
  private couleurTemp = new THREE.Color()
  private axeY = new THREE.Vector3(0, 1, 0)
  private axeX = new THREE.Vector3(1, 0, 0)
  /** Vrai quand les lots portent leur silhouette simplifiee. */
  private loin = false
  /** Derniere echelle appliquee, en pixels par metre. Pour le journal. */
  private pixelsParMetre = 0

  /**
   * Auto-pilotage du niveau de detail, greffe sur chaque lot intact.
   *
   * On ignore la passe d'ombre (elle rend dans une cible hors ecran) et tout ce
   * qui n'est pas la camera orthographique du jeu: sinon le frustum de la
   * lumiere, bien plus large, ferait croire a un dezoom permanent.
   */
  private piloteDetail = (rendu: THREE.WebGLRenderer, _scene: THREE.Scene, camera: THREE.Camera): void => {
    if (rendu.getRenderTarget() !== null) return
    const ortho = camera as THREE.OrthographicCamera
    if (!ortho.isOrthographicCamera) return
    this.reglerDetail(ortho.top, rendu.domElement.height)
  }

  constructor(scene: THREE.Scene, ressources: Ressource[], tempsJeu: number) {
    this.scene = scene
    this.groupe = new THREE.Group()
    this.groupe.name = 'ressources'
    scene.add(this.groupe)
    this.materiau = new THREE.MeshLambertMaterial({ vertexColors: true })

    // Regrouper par espece et par chunk.
    const paquets = new Map<string, { kind: RessourceKind; noeuds: Ressource[] }>()
    for (const r of ressources) {
      const cx = Math.floor(r.pos.x / CHUNK_SIZE)
      const cz = Math.floor(r.pos.z / CHUNK_SIZE)
      const cle = `${r.kind}:${cx}:${cz}`
      const p = paquets.get(cle)
      if (p) p.noeuds.push(r)
      else paquets.set(cle, { kind: r.kind, noeuds: [r] })
    }

    // Les geometries sont partagees entre tous les lots d'une meme espece.
    const geoIntact = new Map<RessourceKind, THREE.BufferGeometry>()
    const geoLoin = new Map<RessourceKind, THREE.BufferGeometry>()
    const geoSouches = new Map<RessourceKind, THREE.BufferGeometry>()

    for (const p of paquets.values()) {
      let gi = geoIntact.get(p.kind)
      if (!gi) {
        gi = FABRIQUES[p.kind]()
        geoIntact.set(p.kind, gi)
      }
      let gl = geoLoin.get(p.kind)
      if (!gl) {
        gl = FABRIQUES_LOIN[p.kind]()
        geoLoin.set(p.kind, gl)
      }
      let gs = geoSouches.get(p.kind)
      if (!gs) {
        gs = geoSouche(p.kind)
        geoSouches.set(p.kind, gs)
      }

      const intact = new THREE.InstancedMesh(gi, this.materiau, p.noeuds.length)
      const souche = new THREE.InstancedMesh(gs, this.materiau, p.noeuds.length)
      intact.name = `ressource:${p.kind}`
      souche.name = `souche:${p.kind}`
      intact.castShadow = true
      souche.receiveShadow = true
      intact.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
      souche.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

      const lot: Lot = { kind: p.kind, intact, souche, noeuds: p.noeuds, geoPres: gi, geoLoin: gl, couvert: null }
      this.lots.push(lot)
      this.groupe.add(intact, souche)
      // Le niveau de detail se pilote tout seul: la premiere instance dessinee
      // de l'image renseigne le zoom courant. `onBeforeRender` recoit le rendu
      // et la camera, et three.js a deja fige la geometrie de l'image en cours,
      // donc l'echange prend effet a l'image suivante — sans dechirure.
      intact.onBeforeRender = this.piloteDetail

      for (let i = 0; i < p.noeuds.length; i++) {
        this.adresses.set(p.noeuds[i]!.id, { lot, instance: i })
        this.ecrireInstance(lot, i, tempsJeu, 0)
      }
      intact.instanceMatrix.needsUpdate = true
      souche.instanceMatrix.needsUpdate = true
      // Sphere englobante serree sur le lot: c'est elle qui permet a three.js
      // d'eliminer le lot entier hors du champ.
      this.calculerBornes(lot)
    }

    this.anneau = new THREE.Mesh(
      new THREE.TorusGeometry(0.95, 0.09, 8, 28),
      new THREE.MeshBasicMaterial({ color: 0xffe066, transparent: true, opacity: 0.9 }),
    )
    this.anneau.rotation.x = -Math.PI / 2
    this.anneau.visible = false
    this.anneau.name = 'surbrillance'
    this.groupe.add(this.anneau)
  }

  /**
   * Sphere englobante du lot. `computeBoundingSphere` d'un InstancedMesh ignore
   * les matrices d'instance dans certaines versions: on la calcule donc a la
   * main, en ajoutant la hauteur des modeles au rayon.
   */
  private calculerBornes(lot: Lot): void {
    let minX = Infinity
    let minZ = Infinity
    let maxX = -Infinity
    let maxZ = -Infinity
    let echelleMax = 1
    for (const r of lot.noeuds) {
      if (r.pos.x < minX) minX = r.pos.x
      if (r.pos.x > maxX) maxX = r.pos.x
      if (r.pos.z < minZ) minZ = r.pos.z
      if (r.pos.z > maxZ) maxZ = r.pos.z
      if (r.scale > echelleMax) echelleMax = r.scale
    }
    const gi = lot.intact.geometry.boundingSphere
    const rayonModele = (gi?.radius ?? 3) * echelleMax
    const cx = (minX + maxX) / 2
    const cz = (minZ + maxZ) / 2
    const demi = Math.hypot(maxX - minX, maxZ - minZ) / 2
    const centre = new THREE.Vector3(cx, 3, cz)
    const rayon = demi + rayonModele + 2

    for (const m of [lot.intact, lot.souche]) {
      m.boundingSphere = new THREE.Sphere(centre.clone(), rayon)
      // Empecher three.js de recalculer une sphere fausse.
      m.computeBoundingSphere = (): void => {}
      m.frustumCulled = true
    }
  }

  private ecrireInstance(lot: Lot, i: number, tempsJeu: number, decalage: number): void {
    const r = lot.noeuds[i]
    if (!r) return
    // Le masque est precalcule par `majBatiments`: cette methode est appelee a
    // chaque image pour les noeuds qui tressaillent, elle ne doit pas balayer
    // la liste des constructions.
    const couvert = lot.couvert ? (lot.couvert[i] ?? 0) !== 0 : false
    const vivant = !couvert && estDisponible(r, tempsJeu)

    this.quatTemp.setFromAxisAngle(this.axeY, r.rot)
    if (decalage !== 0) {
      const penche = new THREE.Quaternion().setFromAxisAngle(this.axeX, decalage)
      this.quatTemp.multiply(penche)
    }
    this.posTemp.set(r.pos.x, r.y, r.pos.z)

    this.echelleTemp.setScalar(vivant ? r.scale : 0)
    this.matriceTemp.compose(this.posTemp, this.quatTemp, this.echelleTemp)
    lot.intact.setMatrixAt(i, this.matriceTemp)

    this.echelleTemp.setScalar(vivant || couvert ? 0 : r.scale)
    this.matriceTemp.compose(this.posTemp, this.quatTemp, this.echelleTemp)
    lot.souche.setMatrixAt(i, this.matriceTemp)

    this.couleurTemp.setHex(r.teinte || 0xffffff)
    lot.intact.setColorAt(i, this.couleurTemp)
    lot.souche.setColorAt(i, this.couleurTemp)
  }

  /**
   * Choisit la geometrie de chaque lot selon la taille apparente du modele.
   *
   * `demiHauteurMonde` est la demi-hauteur du frustum orthographique en metres
   * (le `zoom` du rig), `hauteurEcran` la hauteur du tampon de dessin. La classe
   * s'en occupe seule a chaque image (voir `piloteDetail`); la methode reste
   * publique pour que le jeu puisse forcer un niveau.
   */
  reglerDetail(demiHauteurMonde: number, hauteurEcran: number): void {
    const pixelsParMetre = hauteurEcran / Math.max(1e-6, demiHauteurMonde * 2)
    this.pixelsParMetre = pixelsParMetre
    // Reference: l'arbre, qui pese a lui seul plus de la moitie des triangles.
    const apparent = HAUTEUR_MODELE.arbre * pixelsParMetre
    const loin = this.loin ? apparent < PIXELS_PRES : apparent < PIXELS_LOIN
    if (loin === this.loin) return
    this.loin = loin
    for (const lot of this.lots) lot.intact.geometry = loin ? lot.geoLoin : lot.geoPres
    journal.debug('ressources', `detail ${loin ? 'silhouette' : 'complet'} (${apparent.toFixed(0)} px par arbre)`)
  }

  /** Vrai quand les silhouettes simplifiees sont en place. Pour les tests. */
  get detailLoin(): boolean {
    return this.loin
  }

  /** Echelle courante, en pixels d'ecran par metre de monde. */
  get echelleDetail(): number {
    return this.pixelsParMetre
  }

  /** Rafraichit un seul noeud (recolte, repousse). */
  majNoeud(r: Ressource, tempsJeu: number): void {
    const a = this.adresses.get(r.id)
    if (!a) return
    a.lot.noeuds[a.instance] = r
    this.ecrireInstance(a.lot, a.instance, tempsJeu, 0)
    a.lot.intact.instanceMatrix.needsUpdate = true
    a.lot.souche.instanceMatrix.needsUpdate = true
    if (a.lot.intact.instanceColor) a.lot.intact.instanceColor.needsUpdate = true
  }

  /**
   * Escamote les noeuds recouverts par une construction.
   *
   * Meme geste et memes raisons que `RenduProps.majCouverture`: on RECALCULE a
   * partir de la liste passee au lieu de poser un drapeau a la pose, pour que
   * demolir rende l'arbre et qu'un batiment arrive par le salon compte tout de
   * suite. La marge est ZERO, exactement celle de l'etape 5 de
   * `validerPlacement`: un noeud qui n'a jamais empeche de batir ne doit jamais
   * disparaitre.
   *
   * Cout: un test de disque par (lot, batiment), puis `couvreLePoint` sur les
   * seuls noeuds des lots traverses. Par evenement, jamais par image.
   */
  majBatiments(batiments: readonly Batiment[], tempsJeu: number): number {
    let caches = 0
    // Un rayon par batiment, calcule UNE fois pour tous les lots.
    // Piste comprise: un arbre qui repousse en travers du decollage est cache
    // exactement comme celui qui repousse dans un salon (`couvreLePoint`).
    const rayons = batiments.map((b) => rayonCouverture(b))

    for (const lot of this.lots) {
      const precedent = lot.couvert
      let courant: Uint8Array | null = null
      const s = lot.intact.boundingSphere

      for (let k = 0; k < batiments.length; k++) {
        const b = batiments[k]
        if (!b) continue
        if (s) {
          const dx = s.center.x - b.pos.x
          const dz = s.center.z - b.pos.z
          const porte = (rayons[k] ?? 0) + s.radius
          if (dx * dx + dz * dz > porte * porte) continue
        }
        for (let i = 0; i < lot.noeuds.length; i++) {
          const r = lot.noeuds[i]
          if (!r || !couvreLePoint(b, r.pos.x, r.pos.z)) continue
          if (!courant) courant = new Uint8Array(lot.noeuds.length)
          courant[i] = 1
        }
      }

      if (courant) for (const v of courant) if (v !== 0) caches++
      if (!precedent && !courant) continue
      lot.couvert = courant
      let change = false
      for (let i = 0; i < lot.noeuds.length; i++) {
        const avant = precedent ? (precedent[i] ?? 0) : 0
        const apres = courant ? (courant[i] ?? 0) : 0
        if (avant === apres) continue
        this.ecrireInstance(lot, i, tempsJeu, 0)
        change = true
      }
      if (change) {
        lot.intact.instanceMatrix.needsUpdate = true
        lot.souche.instanceMatrix.needsUpdate = true
      }
    }
    return caches
  }

  /** Nombre de noeuds actuellement escamotes par une construction. */
  nombreCouverts(): number {
    let n = 0
    for (const lot of this.lots) {
      if (!lot.couvert) continue
      for (const v of lot.couvert) if (v !== 0) n++
    }
    return n
  }

  /** Rafraichit tout: a appeler apres une repousse groupee. */
  majTout(tempsJeu: number): void {
    for (const lot of this.lots) {
      for (let i = 0; i < lot.noeuds.length; i++) this.ecrireInstance(lot, i, tempsJeu, 0)
      lot.intact.instanceMatrix.needsUpdate = true
      lot.souche.instanceMatrix.needsUpdate = true
    }
  }

  /** Met l'anneau autour du noeud vise, ou le cache si null. */
  surbriller(r: Ressource | null): void {
    if (!r) {
      this.anneau.visible = false
      this.anneauVisible = false
      return
    }
    this.anneau.position.set(r.pos.x, r.y + 0.08, r.pos.z)
    const rayon = r.kind === 'arbre' || r.kind === 'palmier' ? 1.1 : 0.85
    this.anneauEchelle = (rayon * r.scale) / 0.95
    this.anneau.scale.setScalar(this.anneauEchelle)
    this.anneau.visible = true
    this.anneauVisible = true
  }

  /** Fait tressaillir un noeud qu'on vient de frapper. */
  secouer(r: Ressource): void {
    const a = this.adresses.get(r.id)
    if (!a) return
    this.secousses.push({ lot: a.lot, instance: a.instance, restant: DUREE_SECOUSSE })
  }

  maj(dt: number, tempsJeu: number): void {
    if (this.anneauVisible) {
      this.tempsAnneau += dt
      const battement = Math.sin(this.tempsAnneau * 6)
      const mt = this.anneau.material as THREE.MeshBasicMaterial
      mt.opacity = 0.65 + battement * 0.25
      this.anneau.rotation.z += dt * 0.9
      this.anneau.scale.setScalar(this.anneauEchelle * (1 + battement * 0.08))
    }

    if (this.secousses.length === 0) return
    for (let s = this.secousses.length - 1; s >= 0; s--) {
      const sec = this.secousses[s]!
      sec.restant -= dt
      const t = clamp(sec.restant / DUREE_SECOUSSE, 0, 1)
      const angle = Math.sin(t * Math.PI * 5) * 0.09 * t
      this.ecrireInstance(sec.lot, sec.instance, tempsJeu, angle)
      sec.lot.intact.instanceMatrix.needsUpdate = true
      if (sec.restant <= 0) {
        this.ecrireInstance(sec.lot, sec.instance, tempsJeu, 0)
        this.secousses.splice(s, 1)
      }
    }
  }

  nombreInstances(): number {
    let n = 0
    for (const lot of this.lots) n += lot.noeuds.length
    return n
  }

  /** Nombre de lots, donc d'appels de dessin potentiels avant elimination. */
  nombreLots(): number {
    return this.lots.length
  }

  dispose(): void {
    const geos = new Set<THREE.BufferGeometry>()
    for (const lot of this.lots) {
      geos.add(lot.geoPres)
      geos.add(lot.geoLoin)
      geos.add(lot.souche.geometry)
      lot.intact.onBeforeRender = (): void => {}
      lot.intact.dispose()
      lot.souche.dispose()
    }
    for (const g of geos) g.dispose()
    this.materiau.dispose()
    this.anneau.geometry.dispose()
    ;(this.anneau.material as THREE.Material).dispose()
    this.lots = []
    this.adresses.clear()
    this.scene.remove(this.groupe)
  }
}
